import React, { useState, useEffect } from 'react';
import { getPlans, createPlan, updatePlan, deletePlan, Plan, Page } from '../api/admin';
import styles from './CommonPage.module.css';
import { Edit, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';

// Redundant Plan interface removed - using the one from ../api/admin



const PlanPage = () => {
    const [data, setData] = useState<Page<Plan> | null>(null);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [isAppModalOpen, setAppModalOpen] = useState(false);
    
    // Form State
    const [editId, setEditId] = useState<number | null>(null);
    const [name, setName] = useState('');
    const [durationDays, setDurationDays] = useState(30);
    const [price, setPrice] = useState(0);
    const [description, setDescription] = useState('');
    const [limitGB, setLimitGB] = useState(10); // Default 10GB
    const [userType, setUserType] = useState<'GENERAL' | 'BUSINESS'>('GENERAL');
    
    // UI Helper State
    const [durationValue, setDurationValue] = useState(1);
    const [unit, setUnit] = useState<'MONTHS' | 'YEARS'>('MONTHS');

    const fetchPlans = async () => {
        setLoading(true);
        try {
            const result = await getPlans(page, 10);
            setData(result);
        } catch (error) {
            console.error('Failed to fetch plans', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPlans();
    }, [page]);

    // Prevent background scrolling when modal is open
    useEffect(() => {
      if (isAppModalOpen) {
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
        document.documentElement.style.overflow = '';
      }
      return () => {
        document.body.style.overflow = '';
        document.documentElement.style.overflow = '';
      };
    }, [isAppModalOpen]);

    const handlePrev = () => {
        if (page > 0) setPage(page - 1);
    };

    const handleNext = () => {
        if (data && page < data.totalPages - 1) setPage(page + 1);
    };

    const handleCreate = () => {
        setEditId(null);
        setName('');
        setDurationDays(30);
        setDurationValue(1);
        setUnit('MONTHS');
        setPrice(0);
        setDescription('');
        setLimitGB(10);
        setUserType('GENERAL');
        setAppModalOpen(true);
    };

    const handleEdit = (plan: Plan) => {
        setEditId(plan.id);
        setName(plan.name);
        setDurationDays(plan.durationDays);
        setPrice(plan.price);
        setDescription(plan.description || '');
        setLimitGB(plan.limitBytes ? plan.limitBytes / (1024 * 1024 * 1024) : 10);
        setUserType(plan.userType || 'GENERAL');
        
        // Reverse engineer unit
        if (plan.durationDays % 365 === 0) {
            setUnit('YEARS');
            setDurationValue(plan.durationDays / 365);
        } else {
            setUnit('MONTHS');
            setDurationValue(parseFloat((plan.durationDays / 30).toFixed(2))); // Handle non-exact months gracefully
        }
        
        setAppModalOpen(true);
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm('정말 삭제하시겠습니까?')) return;
        try {
            await deletePlan(id);
            fetchPlans();
        } catch (error) {
            console.error('Failed to delete plan', error);
            alert('삭제 실패');
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            // Calculate actual days
            let calculatedDays = durationValue;
            if (unit === 'MONTHS') calculatedDays = Math.round(durationValue * 30);
            if (unit === 'YEARS') calculatedDays = Math.round(durationValue * 365);
            
            // Convert GB to bytes
            const limitBytes = Math.round(limitGB * 1024 * 1024 * 1024);
            
            const payload = { name, durationDays: calculatedDays, price, description, limitBytes, userType };
            if (editId) {
                await updatePlan(editId, payload);
            } else {
                await createPlan(payload);
            }
            setAppModalOpen(false);
            fetchPlans();
        } catch (error) {
            console.error('Failed to save plan', error);
            alert('저장 실패');
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1 className={styles.title}>요금제 관리</h1>
                <button className={styles.primaryBtn} onClick={handleCreate}>+ 요금제 생성</button>
            </div>

            <div className={styles.tableContainer}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>요금제 명</th>
                            <th>대상</th>
                            <th>기간 (일)</th>
                            <th>가격 (원)</th>
                            <th>관리</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={6} style={{ textAlign: 'center', padding: '24px' }}>로딩 중...</td></tr>
                        ) : data?.content.map((plan: Plan) => (
                            <tr key={plan.id}>
                                <td data-label="ID">{plan.id}</td>
                                <td data-label="요금제 명">{plan.name}</td>
                                <td data-label="대상">
                                    <span style={{ fontSize: '0.8rem', padding: '2px 6px', backgroundColor: '#e5e7eb', borderRadius: '4px', whiteSpace: 'nowrap' }}>
                                        {plan.userType === 'BUSINESS' ? '사업자' : '일반'}
                                    </span>
                                </td>
                                <td data-label="기간 (일)">{plan.durationDays}</td>
                                <td data-label="가격 (원)">{plan.price.toLocaleString()}</td>
                                <td data-label="관리">
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <button className={styles.iconBtn} onClick={() => handleEdit(plan)} title="수정">
                                            <Edit size={16} />
                                        </button>
                                        <button className={styles.deleteBtn} onClick={() => handleDelete(plan.id)} title="삭제">
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {data?.content.length === 0 && !loading && (
                             <tr>
                                 <td colSpan={6} style={{ textAlign: 'center', padding: '24px', color: '#6b7280' }}>등록된 요금제가 없습니다.</td>
                             </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className={styles.pagination}>
                <button onClick={handlePrev} disabled={page === 0}>
                    <ChevronLeft size={16} /> 이전
                </button>
                <span>
                    {data?.number !== undefined ? data.number + 1 : 1} / {data?.totalPages || 1} 페이지
                </span>
                <button onClick={handleNext} disabled={!data || page >= data.totalPages - 1}>
                    다음 <ChevronRight size={16} />
                </button>
            </div>

            {isAppModalOpen && (
                <div className={styles.modalOverlay}>
                    <div className={styles.modal}>
                        <h2>{editId ? '요금제 수정' : '요금제 생성'}</h2>
                        <form onSubmit={handleSubmit}>
                            <div className={styles.formGroup}>
                                <label>대상 회원 유형</label>
                                <div style={{ display: 'flex', gap: '16px', marginBottom: '8px' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                                        <input
                                            type="radio"
                                            name="userType"
                                            value="GENERAL"
                                            checked={userType === 'GENERAL'}
                                            onChange={() => setUserType('GENERAL')}
                                        /> 일반 회원
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                                        <input
                                            type="radio"
                                            name="userType"
                                            value="BUSINESS"
                                            checked={userType === 'BUSINESS'}
                                            onChange={() => setUserType('BUSINESS')}
                                        /> 사업자 회원
                                    </label>
                                </div>
                            </div>
                            <div className={styles.formGroup}>
                                <label>요금제 명</label>
                                <input type="text" value={name} onChange={e => setName(e.target.value)} required />
                            </div>
                            <div className={styles.formGroup}>
                                <label>기간 설정</label>
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <input 
                                        type="number" 
                                        value={durationValue} 
                                        onChange={e => setDurationValue(Number(e.target.value))} 
                                        required 
                                        style={{ width: '120px', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                                    />
                                    <select 
                                        value={unit} 
                                        onChange={(e) => setUnit(e.target.value as any)}
                                        style={{ padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px', minWidth: '120px' }}
                                    >
                                        <option value="MONTHS">월 (Months)</option>
                                        <option value="YEARS">년 (Years)</option>
                                    </select>
                                </div>
                                <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '4px' }}>
                                    자동 계산: {unit === 'MONTHS' ? Math.round(durationValue * 30) : Math.round(durationValue * 365)}일
                                </div>
                            </div>
                            <div className={styles.formGroup}>
                                <label>Relay 월 이용가능량 (GB)</label>
                                <input 
                                    type="number" 
                                    value={limitGB} 
                                    onChange={e => setLimitGB(Number(e.target.value))} 
                                    required 
                                    min="0.1"
                                    step="0.1"
                                    placeholder="예: 10"
                                />
                                <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '4px' }}>
                                    릴레이 서버를 통한 월간 트래픽 제한량 (소수점 가능)
                                </div>
                            </div>
                            <div className={styles.formGroup}>
                                <label>가격 (원)</label>
                                <input type="number" value={price} onChange={e => setPrice(Number(e.target.value))} required />
                            </div>
                            <div className={styles.formGroup}>
                                <label>설명</label>
                                <textarea 
                                    value={description} 
                                    onChange={e => setDescription(e.target.value)} 
                                    rows={5}
                                    style={{width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px'}}
                                />
                            </div>
                            <div className={styles.actionButtons} style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '24px' }}>
                                <button type="button" className={styles.cancelBtn} onClick={() => setAppModalOpen(false)}>취소</button>
                                <button type="submit" className={styles.submitBtn}>저장</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PlanPage;
